import { gql } from "@apollo/client";

import { CompanyData } from "../utils/types";
import { mutate } from "./client";

export const DASHBOARD_QUERY = gql`
  query Dashboard {
    me {
      companies {
        id
        name
        tagline
        slug
        logo
        description
        prompt
        roles { title description }
        launchPage {
          id
          slug
          companyId
          pageViews
          heroImage
          waitlist { email }
        }
      }
    }
  }
`;

export const COMPANY_WORKSPACE_QUERY = gql`
  query CompanyWorkspace($id: ID!) {
    company(id: $id) {
      id
      name
      description
      roles { title description }
      tasks {
        id
        title
        description
        acceptanceCriteria
        role
        priority
        status
      }
    }
  }
`;

export const PUBLIC_LAUNCH_QUERY = gql`
  query PublicLaunch($slug: String!) {
    launchPage(slug: $slug) {
      id
      slug
      companyId
      heroImage
      pageViews
      waitlist { email }
      company {
        id
        name
        tagline
        logo
      }
    }
  }
`;

const CREATE_COMPANY = gql`
  mutation CreateCompany($input: CreateCompanyInput!) {
    createCompany(input: $input) {
      id
      name
      slug
    }
  }
`;

const UPDATE_COMPANY = gql`
  mutation UpdateCompany($id: ID!, $input: UpdateCompanyInput!) {
    updateCompany(id: $id, input: $input) {
      id
      name
      slug
      tagline
      logo
      roles { title description }
    }
  }
`;

const DELETE_COMPANY = gql`
  mutation DeleteCompany($id: ID!) {
    deleteCompany(id: $id)
  }
`;

const CREATE_LAUNCH_PAGE = gql`
  mutation CreateLaunchPage($companyId: ID!, $slug: String!) {
    createLaunchPage(companyId: $companyId, slug: $slug) {
      id
      slug
    }
  }
`;

const JOIN_WAITLIST = gql`
  mutation JoinWaitlist($slug: String!, $email: String!) {
    joinWaitlist(slug: $slug, email: $email) {
      slug
      waitlist { email }
    }
  }
`;

const CREATE_TASK = gql`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      id
    }
  }
`;

const DELETE_TASKS = gql`
  mutation DeleteTasks($companyId: ID!) {
    deleteTasks(companyId: $companyId)
  }
`;

export type GqlRole = { title: string; description: string };

export type GqlTask = {
  id: string;
  title?: string | null;
  description?: string | null;
  acceptanceCriteria: string[];
  role?: string | null;
  priority?: string | null;
  status?: string | null;
};

export type GqlLaunchPage = {
  id: string;
  slug: string;
  companyId: string;
  pageViews: number;
  heroImage: string;
  waitlist: { email: string }[];
};

export type GqlCompany = {
  id: string;
  name: string;
  tagline?: string | null;
  slug?: string | null;
  logo?: string[] | null;
  description?: string | null;
  prompt?: string | null;
  roles?: GqlRole[] | null;
  launchPage?: GqlLaunchPage | null;
  tasks?: GqlTask[] | null;
};

export type DashboardData = {
  me: { companies: GqlCompany[] } | null;
};

export type CompanyWorkspaceData = {
  company: GqlCompany | null;
};

export type PublicLaunchData = {
  launchPage: (GqlLaunchPage & { company: GqlCompany | null }) | null;
};

export function toCompany(company: GqlCompany): CompanyData & { prompt?: string | null } {
  return {
    _id: company.id,
    name: company.name || "",
    tagline: company.tagline || "",
    slug: company.slug || "",
    logo: company.logo || [],
    description: company.description || "",
    roles: company.roles || [],
    prompt: company.prompt,
  };
}

type CompanyInput = {
  name?: string;
  tagline?: string;
  slug?: string;
  prompt?: string;
  description?: string;
  logo?: string | string[];
  roles?: GqlRole[];
};

export function createCompany(companyData: CompanyInput) {
  const logo = Array.isArray(companyData.logo) ? companyData.logo : companyData.logo ? [companyData.logo] : [];
  return mutate<{ createCompany: { id: string; name: string; slug: string } }>(CREATE_COMPANY, {
    input: {
      name: companyData.name,
      tagline: companyData.tagline,
      slug: companyData.slug,
      prompt: companyData.prompt,
      description: companyData.description,
      logo,
      ...(companyData.roles ? { roles: companyData.roles } : {}),
    },
  }, ["Dashboard"]).then((data) => ({
    message: "Company created successfully",
    company: { ...data.createCompany, _id: data.createCompany.id },
  }));
}

export function updateCompany(id: string, data: CompanyInput) {
  const logos = Array.isArray(data.logo) ? data.logo.filter((item): item is string => Boolean(item)) : [];
  const logo = Array.isArray(data.logo) ? logos[logos.length - 1] : data.logo;
  return mutate(UPDATE_COMPANY, {
    id,
    input: {
      ...(data.tagline !== undefined ? { tagline: data.tagline } : {}),
      ...(data.name !== undefined ? { name: data.name } : {}),
      ...(data.slug !== undefined ? { slug: data.slug } : {}),
      ...(data.prompt !== undefined ? { prompt: data.prompt } : {}),
      ...(data.description !== undefined ? { description: data.description } : {}),
      ...(data.roles !== undefined ? { roles: data.roles } : {}),
      ...(logo ? { logo } : {}),
    },
  }, ["Dashboard", "CompanyWorkspace"]);
}

export function deleteCompany(id: string) {
  return mutate(DELETE_COMPANY, { id }, ["Dashboard"]).then(() => ({
    message: "Company and tasks deleted successfully",
  }));
}

export function createLaunchPage(input: { companyId: string; slug: string }) {
  return mutate<{ createLaunchPage: { id: string; slug: string } }>(CREATE_LAUNCH_PAGE, input, ["Dashboard"]).then((data) => ({
    message: "Launch page created successfully",
    launchPage: data.createLaunchPage,
  }));
}

export function joinWaitlist(input: { slug: string; email: string }) {
  return mutate(JOIN_WAITLIST, input, ["PublicLaunch"]).then(() => ({
    message: "Successfully added to waitlist",
  }));
}

export function createTask(input: Record<string, unknown>) {
  return mutate(CREATE_TASK, { input }, ["CompanyWorkspace"]);
}

export function deleteTasks(companyId: string) {
  return mutate(DELETE_TASKS, { companyId }, ["CompanyWorkspace"]);
}
